import { GenericError, LoadingSpinner, SurveyJs } from '@psyomics/ui-components';
import React, { memo, useMemo, useState } from 'react';
import { Model } from 'survey-react-ui';

import { ReferralInvite } from '../../referral/generated/referral';
import { useReferralInviteQuery } from '../../referral/hooks/use-referral-invite-query';
import { SurveyResponse, useSurveyResponse } from '../../survey-responses/hooks/use-survey-response';
import { useUpdateSurveyResponse } from '../../survey-responses/hooks/use-update-survey-response';

import { EndScreen } from './end-screen';
import { reloadPage } from './reload-page';
import { usePrepareSurvey } from './use-prepare-survey';
import { useSurveyHandlers } from './use-survey-handlers';

type SurveyData = Record<string, unknown>;

type SurveyBuilderProps = {
  readonly initialisedNow?: boolean;
  readonly invitePublicId: string;
  readonly surveyJson: object;
  readonly surveyResponseId: string;
  readonly reviewMode?: boolean;
};

const RenderSurvey = memo(({ model }: { readonly model: SurveyJs.Model }) => <SurveyJs.SurveyRenderer model={model} />);

type ErrorTypes = 'SurveyNotFound' | 'SurveyResponseConflict';
const errorMap: Record<ErrorTypes, { titleKey: string; titleComponents?: Record<string, React.ReactElement> }> = {
  SurveyNotFound: { titleKey: 'page.viewSurvey.errors.notFound' },
  SurveyResponseConflict: {
    titleKey: 'page.viewSurvey.errors.conflict',
    titleComponents: {
      refreshLink: (
        <button className="text-blue-600 underline cursor-pointer" onClick={reloadPage} type="button">
          refresh
        </button>
      ),
    },
  },
};

const useSurveyModel = (surveyJson: object, reviewMode: boolean) =>
  useMemo(() => {
    const model = new Model(surveyJson);
    if (reviewMode) model.mode = 'display';
    return model;
  }, [surveyJson, reviewMode]);

const useInviteParams = (invite?: ReferralInvite) =>
  useMemo(() => {
    const referralSession = invite?.referralSession;
    const referral = referralSession?.referral;
    const organisation = referral?.organisation;
    const service = referral?.service;
    return {
      organisationCode: organisation?.code,
      serviceCode: service?.code,
      isExpired: referralSession?.status === 'expired' || referral?.status === 'cancelled',
    };
  }, [invite]);

const shouldShowEndScreen = (reviewMode: boolean, surveyResults?: SurveyData | null, surveyResponse?: SurveyResponse) =>
  surveyResults || (surveyResponse?.completedAt && !reviewMode);

export const SurveyBuilder: React.FC<SurveyBuilderProps> = ({
  surveyJson,
  invitePublicId,
  surveyResponseId,
  reviewMode = false,
  initialisedNow = false,
}) => {
  const [surveyResults, setSurveyResults] = useState<SurveyData | null>(null);
  const [{ errorMessage }, updateSurveyAnswers] = useUpdateSurveyResponse();
  const { data: surveyResponse, fetching: surveyResponseFetching } = useSurveyResponse(surveyResponseId);
  const [invite] = useReferralInviteQuery(invitePublicId);
  const { organisationCode, serviceCode, isExpired } = useInviteParams(invite);
  const surveyModel = useSurveyModel(surveyJson, reviewMode);

  const ready = usePrepareSurvey({
    fetchingResponses: surveyResponseFetching,
    initialisedNow,
    model: surveyModel,
    reviewMode,
    surveyResponse,
  });
  useSurveyHandlers({
    invitePublicId,
    organisationCode,
    ready,
    reviewMode,
    serviceCode,
    setSurveyResults,
    surveyModel,
    surveyResponseId,
    surveyResponseUpdatedAt: surveyResponse?.updatedAt,
    updateSurveyAnswers,
  });

  if (!ready) return <LoadingSpinner />;
  if (!reviewMode && isExpired) throw new Error('referral cancelled or expired');
  if (errorMessage) return <GenericError {...errorMap[errorMessage]} />;
  if (shouldShowEndScreen(reviewMode, surveyResults, surveyResponse)) return <EndScreen invite={invite} />;
  return (
    <div className="flex justify-center bg-neutral-100">
      <div className="w-full">
        <RenderSurvey model={surveyModel} />
      </div>
    </div>
  );
};
